import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAudits, getAudit, createAudit, updateAudit, addAuditFinding, getUsers, createCAPA, getIOSATemplates, createAuditFromIOSA } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Plus, Search, ClipboardCheck, Eye, Edit, ChevronLeft, Calendar,
  AlertTriangle, CheckCircle, XCircle, Play, FileText, Users, Trash2, Shield, MessageSquare, Plane
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import CollaborationPanel from '@/components/CollaborationPanel';
import AssetSelector, { AssetDisplay } from '@/components/AssetSelector';
import PolishButton from '@/components/PolishButton';

const AUDIT_TYPES = [
  { value: 'internal', label: 'Internal Audit', label_ar: 'تدقيق داخلي' },
  { value: 'external', label: 'External Audit', label_ar: 'تدقيق خارجي' },
  { value: 'regulatory', label: 'Regulatory Audit', label_ar: 'تدقيق تنظيمي' },
  { value: 'IOSA', label: 'IOSA Audit', label_ar: 'تدقيق IOSA' },
];

const AUDIT_STATUSES = [
  { value: 'planned', label: 'Planned', label_ar: 'مخطط', icon: Calendar, color: 'bg-blue-900/30 text-blue-400 border-blue-900/50' },
  { value: 'in_progress', label: 'In Progress', label_ar: 'قيد التنفيذ', icon: Play, color: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50' },
  { value: 'completed', label: 'Completed', label_ar: 'مكتمل', icon: CheckCircle, color: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50' },
  { value: 'cancelled', label: 'Cancelled', label_ar: 'ملغى', icon: XCircle, color: 'bg-slate-800 text-slate-400 border-slate-700' },
];

const FINDING_SEVERITIES = [
  { value: 'major', label: 'Major', label_ar: 'رئيسي', color: 'bg-red-600 text-white' },
  { value: 'minor', label: 'Minor', label_ar: 'ثانوي', color: 'bg-orange-600 text-white' },
  { value: 'observation', label: 'Observation', label_ar: 'ملاحظة', color: 'bg-slate-600 text-white' },
];

export default function AuditsPage({ initialMode }) {
  const { t } = useTranslation();
  const { api, user, token } = useAuth();
  const { language, isRTL } = useLanguage();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialMode || 'list');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [users, setUsers] = useState([]);
  const [showFindingDialog, setShowFindingDialog] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    audit_type: 'internal',
    scope: '',
    scope_ar: '',
    planned_date: new Date().toISOString().split('T')[0],
    department: '',
    department_ar: '',
    auditor_ids: [],
    // Asset linking fields
    aircraft_id: null,
    aircraft_registration: null,
    engine_id: null,
    component_id: null,
  });

  const [findingForm, setFindingForm] = useState({
    description: '',
    description_ar: '',
    severity: 'minor',
    regulatory_reference: '',
    evidence: ''
  });

  const [showCAPADialog, setShowCAPADialog] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [capaForm, setCAPAForm] = useState({
    title: '',
    description: '',
    capa_type: 'corrective',
    due_date: ''
  });

  // IOSA Template State
  const [showIOSADialog, setShowIOSADialog] = useState(false);
  const [iosaTemplates, setIOSATemplates] = useState(null);
  const [selectedIOSASection, setSelectedIOSASection] = useState('');
  const [iosaTitle, setIOSATitle] = useState('');
  const [iosaDate, setIOSADate] = useState(new Date().toISOString().split('T')[0]);
  const [creatingIOSA, setCreatingIOSA] = useState(false);

  useEffect(() => {
    if (mode === 'list') {
      fetchAudits();
      fetchUsers();
    }
  }, [mode]);

  const fetchAudits = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.audit_type = filterType;
      const response = await getAudits(api, params);
      setAudits(response.data);
    } catch (error) {
      console.error('Failed to fetch audits:', error);
      toast.error('Failed to load audits');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers(api);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchIOSATemplates = async () => {
    try {
      const response = await getIOSATemplates(api);
      setIOSATemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch IOSA templates:', error);
    }
  };

  const handleCreateIOSAAudit = async () => {
    if (!selectedIOSASection) {
      toast.error('Please select an IOSA section');
      return;
    }
    
    try {
      setCreatingIOSA(true);
      await createAuditFromIOSA(api, selectedIOSASection, iosaTitle || null, iosaDate || null);
      toast.success(`IOSA ${selectedIOSASection} audit created successfully`);
      setShowIOSADialog(false);
      setSelectedIOSASection('');
      setIOSATitle('');
      fetchAudits();
    } catch (error) {
      console.error('Failed to create IOSA audit:', error);
      toast.error('Failed to create IOSA audit');
    } finally {
      setCreatingIOSA(false);
    }
  };

  const fetchAuditDetails = async (auditId) => {
    try {
      const response = await getAudit(api, auditId);
      setSelectedAudit(response.data);
    } catch (error) {
      console.error('Failed to fetch audit details:', error);
      toast.error('Failed to load audit details');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createAudit(api, {
        ...formData,
        planned_date: new Date(formData.planned_date).toISOString(),
      });
      toast.success('Audit scheduled successfully');
      setMode('list');
      resetForm();
    } catch (error) {
      console.error('Failed to create audit:', error);
      toast.error('Failed to create audit');
    }
  };

  const handleUpdate = async (updates) => {
    if (!selectedAudit) return;
    try {
      const response = await updateAudit(api, selectedAudit.id, updates);
      setSelectedAudit(response.data);
      toast.success('Audit updated successfully');
      fetchAudits();
    } catch (error) {
      console.error('Failed to update audit:', error);
      toast.error('Failed to update audit');
    }
  };

  const handleStatusChange = async (newStatus) => {
    const updates = { status: newStatus };
    if (newStatus === 'in_progress') {
      updates.actual_date = new Date().toISOString();
    }
    if (newStatus === 'completed') {
      updates.completed_at = new Date().toISOString();
    }
    await handleUpdate(updates);
  };

  const handleAddFinding = async (e) => {
    e.preventDefault();
    if (!selectedAudit) return;
    try {
      await addAuditFinding(api, selectedAudit.id, findingForm);
      toast.success('Finding added successfully');
      setShowFindingDialog(false);
      setFindingForm({ description: '', description_ar: '', severity: 'minor', regulatory_reference: '', evidence: '' });
      fetchAuditDetails(selectedAudit.id);
    } catch (error) {
      console.error('Failed to add finding:', error);
      toast.error('Failed to add finding');
    }
  };

  const openCAPADialog = (finding) => {
    setSelectedFinding(finding);
    // Pre-fill CAPA form with finding data
    setCAPAForm({
      title: `CAPA for: ${finding.description.substring(0, 50)}...`,
      description: `Audit Finding: ${finding.description}\n\nRegulatory Reference: ${finding.regulatory_reference || 'N/A'}\n\nEvidence: ${finding.evidence || 'N/A'}`,
      capa_type: finding.severity === 'major' ? 'corrective' : 'preventive',
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
    });
    setShowCAPADialog(true);
  };

  const handleCreateCAPA = async (e) => {
    e.preventDefault();
    if (!selectedAudit || !selectedFinding) return;
    try {
      const capaData = {
        ...capaForm,
        source: 'audit',
        source_id: selectedAudit.id,
        finding_id: selectedFinding.id,
        due_date: new Date(capaForm.due_date).toISOString()
      };
      await createCAPA(api, capaData);
      toast.success('CAPA created and linked to finding');
      setShowCAPADialog(false);
      setSelectedFinding(null);
      setCAPAForm({ title: '', description: '', capa_type: 'corrective', due_date: '' });
      fetchAuditDetails(selectedAudit.id);
    } catch (error) {
      console.error('Failed to create CAPA:', error);
      toast.error('Failed to create CAPA');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', title_ar: '', audit_type: 'internal', scope: '', scope_ar: '',
      planned_date: new Date().toISOString().split('T')[0], department: '', department_ar: '', auditor_ids: [],
      aircraft_id: null, aircraft_registration: null, engine_id: null, component_id: null,
    });
  };

  const handleViewAudit = (audit) => {
    setSelectedAudit(audit);
    setMode('view');
  };

  const handleEditAudit = (audit) => {
    setSelectedAudit(audit);
    setFormData({
      title: audit.title || '',
      title_ar: audit.title_ar || '',
      audit_type: audit.audit_type || 'internal',
      scope: audit.scope || '',
      scope_ar: audit.scope_ar || '',
      planned_date: audit.planned_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      department: audit.department || '',
      department_ar: audit.department_ar || '',
      auditor_ids: audit.auditor_ids || [],
      aircraft_id: audit.aircraft_id || null,
      aircraft_registration: audit.aircraft_registration || null,
      engine_id: audit.engine_id || null,
      component_id: audit.component_id || null,
    });
    setMode('edit');
  };

  const filteredAudits = audits.filter(a => 
    a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.audit_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusInfo = (status) => AUDIT_STATUSES.find(s => s.value === status) || AUDIT_STATUSES[0];
  const getTypeInfo = (type) => AUDIT_TYPES.find(t => t.value === type) || AUDIT_TYPES[0];
  const getSeverityInfo = (severity) => FINDING_SEVERITIES.find(s => s.value === severity) || FINDING_SEVERITIES[1];

  // VIEW MODE
  if (mode === 'view' && selectedAudit) {
    const statusInfo = getStatusInfo(selectedAudit.status);
    const typeInfo = getTypeInfo(selectedAudit.audit_type);
    const StatusIcon = statusInfo.icon;

    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setMode('list'); setSelectedAudit(null); }} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sky-400">{selectedAudit.audit_number}</span>
                <Badge className={`${statusInfo.color} border`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {language === 'ar' ? statusInfo.label_ar : statusInfo.label}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold text-white font-barlow mt-1">{selectedAudit.title}</h1>
            </div>
          </div>
          <div className="flex gap-2">
            {selectedAudit.status === 'planned' && (
              <Button onClick={() => handleStatusChange('in_progress')} className="bg-yellow-600 hover:bg-yellow-700">
                <Play className="w-4 h-4 mr-2" />Start Audit
              </Button>
            )}
            {selectedAudit.status === 'in_progress' && (
              <Button onClick={() => handleStatusChange('completed')} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="w-4 h-4 mr-2" />Complete Audit
              </Button>
            )}
            <Button variant="outline" onClick={() => handleEditAudit(selectedAudit)} className="border-slate-700 text-slate-300">
              <Edit className="w-4 h-4 mr-2" />Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCollaboration(!showCollaboration)}
              className={`border-slate-700 ${showCollaboration ? 'bg-cyan-600/20 border-cyan-600/50 text-cyan-400' : 'text-slate-300'}`}
              data-testid="toggle-collaboration-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Collaborate
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Audit Details */}
          <Card className="bg-slate-900/60 border-slate-800 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-sky-400" />
                Audit Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-sm">Audit Type</Label>
                  <p className="text-white capitalize">{language === 'ar' ? typeInfo.label_ar : typeInfo.label}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Department</Label>
                  <p className="text-white">{selectedAudit.department || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Planned Date</Label>
                  <p className="text-white">{format(new Date(selectedAudit.planned_date), 'PPP')}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Actual Date</Label>
                  <p className="text-white">{selectedAudit.actual_date ? format(new Date(selectedAudit.actual_date), 'PPP') : '-'}</p>
                </div>
              </div>
              <div>
                <Label className="text-slate-500 text-sm">Scope</Label>
                <p className="text-slate-300 mt-1">{selectedAudit.scope}</p>
              </div>
              {selectedAudit.summary && (
                <div>
                  <Label className="text-slate-500 text-sm">Summary</Label>
                  <p className="text-slate-300 mt-1">{selectedAudit.summary}</p>
                </div>
              )}
              {selectedAudit.recommendations && (
                <div>
                  <Label className="text-slate-500 text-sm">Recommendations</Label>
                  <p className="text-slate-300 mt-1">{selectedAudit.recommendations}</p>
                </div>
              )}
              
              {/* Linked Assets */}
              {(selectedAudit.aircraft_id || selectedAudit.engine_id || selectedAudit.component_id) && (
                <div className="pt-4 border-t border-slate-800">
                  <Label className="text-slate-500 text-sm flex items-center gap-2">
                    <Plane className="w-4 h-4" />
                    Linked Assets
                  </Label>
                  <div className="mt-2">
                    <AssetDisplay
                      aircraftId={selectedAudit.aircraft_id}
                      aircraftRegistration={selectedAudit.aircraft_registration}
                      engineId={selectedAudit.engine_id}
                      engineSerial={selectedAudit.engine_serial}
                      componentId={selectedAudit.component_id}
                      componentPartNumber={selectedAudit.component_part_number}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Findings Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-3 bg-red-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-red-400">{selectedAudit.findings?.filter(f => f.severity === 'major').length || 0}</p>
                  <p className="text-xs text-slate-400">Major</p>
                </div>
                <div className="p-3 bg-orange-950/30 rounded-lg">
                  <p className="text-2xl font-bold text-orange-400">{selectedAudit.findings?.filter(f => f.severity === 'minor').length || 0}</p>
                  <p className="text-xs text-slate-400">Minor</p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-300">{selectedAudit.findings?.filter(f => f.severity === 'observation').length || 0}</p>
                  <p className="text-xs text-slate-400">Obs.</p>
                </div>
              </div>
              <div className="pt-2 border-t border-slate-800">
                <p className="text-sm text-slate-500">Total Findings</p>
                <p className="text-3xl font-bold text-white">{selectedAudit.findings?.length || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Findings Section */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Audit Findings
              </CardTitle>
              {(selectedAudit.status === 'in_progress' || selectedAudit.status === 'completed') && (
                <Button size="sm" onClick={() => setShowFindingDialog(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-finding-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Finding
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedAudit.findings?.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No findings recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedAudit.findings?.map((finding, idx) => {
                  const severityInfo = getSeverityInfo(finding.severity);
                  return (
                    <div key={finding.id || idx} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={severityInfo.color}>
                              {language === 'ar' ? severityInfo.label_ar : severityInfo.label}
                            </Badge>
                            {finding.regulatory_reference && (
                              <span className="text-xs text-slate-500">{finding.regulatory_reference}</span>
                            )}
                          </div>
                          <p className="text-slate-300">{finding.description}</p>
                          {finding.evidence && (
                            <p className="text-sm text-slate-500 mt-2">Evidence: {finding.evidence}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          {finding.capa_id ? (
                            <Badge variant="outline" className="border-emerald-600 text-emerald-400">
                              <Shield className="w-3 h-3 mr-1" />
                              CAPA Linked
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openCAPADialog(finding)}
                              className="border-orange-600 text-orange-400 hover:bg-orange-950/30"
                              data-testid={`create-capa-btn-${idx}`}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Create CAPA
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Finding Dialog */}
        <Dialog open={showFindingDialog} onOpenChange={setShowFindingDialog}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Add Audit Finding
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddFinding} className="space-y-4 py-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Description *</Label>
                  <PolishButton
                    text={findingForm.description}
                    kind="audit"
                    token={token}
                    onApply={(polished) => setFindingForm({ ...findingForm, description: polished })}
                  />
                </div>
                <Textarea
                  value={findingForm.description}
                  onChange={(e) => setFindingForm({...findingForm, description: e.target.value})}
                  required
                  rows={3}
                  className="bg-slate-950 border-slate-800 mt-1"
                  placeholder="Describe the finding..."
                  data-testid="finding-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Severity *</Label>
                  <Select value={findingForm.severity} onValueChange={(v) => setFindingForm({...findingForm, severity: v})}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="finding-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {FINDING_SEVERITIES.map(s => (
                        <SelectItem key={s.value} value={s.value}>
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${s.color.split(' ')[0]}`} />
                            {language === 'ar' ? s.label_ar : s.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Regulatory Reference</Label>
                  <Input
                    value={findingForm.regulatory_reference}
                    onChange={(e) => setFindingForm({...findingForm, regulatory_reference: e.target.value})}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="e.g., GACA Part 5.3.1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Evidence</Label>
                <Textarea
                  value={findingForm.evidence}
                  onChange={(e) => setFindingForm({...findingForm, evidence: e.target.value})}
                  rows={2}
                  className="bg-slate-950 border-slate-800 mt-1"
                  placeholder="Document evidence of the finding..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-finding-btn">Add Finding</Button>
                <Button type="button" variant="outline" onClick={() => setShowFindingDialog(false)} className="border-slate-700 text-slate-300">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Collaboration Panel */}
        {showCollaboration && (
          <CollaborationPanel
            entityType="audit"
            entityId={selectedAudit.id}
            entityTitle={selectedAudit.title}
            apiClient={api}
            currentUser={user}
            token={token}
            isOpen={showCollaboration}
            onClose={() => setShowCollaboration(false)}
          />
        )}

        {/* Create CAPA Dialog */}
        <Dialog open={showCAPADialog} onOpenChange={setShowCAPADialog}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-400" />
                Create CAPA from Finding
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCAPA} className="space-y-4 py-4">
              {selectedFinding && (
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-sm text-slate-500 mb-1">Source Finding</p>
                  <p className="text-slate-300 text-sm">{selectedFinding.description}</p>
                </div>
              )}
              <div>
                <Label className="text-slate-300">CAPA Title *</Label>
                <Input
                  value={capaForm.title}
                  onChange={(e) => setCAPAForm({...capaForm, title: e.target.value})}
                  required
                  className="bg-slate-950 border-slate-800 mt-1"
                  data-testid="capa-title-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Description *</Label>
                  <PolishButton
                    text={capaForm.description}
                    kind="capa"
                    token={token}
                    onApply={(polished) => setCAPAForm({ ...capaForm, description: polished })}
                  />
                </div>
                <Textarea
                  value={capaForm.description}
                  onChange={(e) => setCAPAForm({...capaForm, description: e.target.value})}
                  required
                  rows={4}
                  className="bg-slate-950 border-slate-800 mt-1"
                  data-testid="capa-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">CAPA Type *</Label>
                  <Select value={capaForm.capa_type} onValueChange={(v) => setCAPAForm({...capaForm, capa_type: v})}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="corrective">Corrective Action</SelectItem>
                      <SelectItem value="preventive">Preventive Action</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Due Date *</Label>
                  <Input
                    type="date"
                    value={capaForm.due_date}
                    onChange={(e) => setCAPAForm({...capaForm, due_date: e.target.value})}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    data-testid="capa-due-date-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="submit-capa-btn">
                  <Shield className="w-4 h-4 mr-2" />
                  Create CAPA
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCAPADialog(false)} className="border-slate-700 text-slate-300">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // CREATE/EDIT MODE
  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => { setMode('list'); setSelectedAudit(null); resetForm(); }} className="text-slate-400 hover:text-white">
            <ChevronLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {mode === 'create' ? t('audits.newAudit') : `Edit: ${selectedAudit?.audit_number}`}
          </h1>
        </div>

        <Card className="bg-slate-900/60 border-slate-800 max-w-3xl">
          <CardContent className="p-6">
            <form onSubmit={mode === 'create' ? handleCreate : (e) => { e.preventDefault(); handleUpdate(formData); setMode('view'); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Title (English) *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    data-testid="audit-title-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">العنوان (العربية)</Label>
                  <Input
                    value={formData.title_ar}
                    onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                    className="bg-slate-950 border-slate-800 mt-1"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t('audits.auditType')} *</Label>
                  <Select value={formData.audit_type} onValueChange={(v) => setFormData({...formData, audit_type: v})}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {AUDIT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          {language === 'ar' ? t.label_ar : t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">{t('audits.plannedDate')} *</Label>
                  <Input
                    type="date"
                    value={formData.planned_date}
                    onChange={(e) => setFormData({...formData, planned_date: e.target.value})}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    data-testid="audit-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Department</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="bg-slate-950 border-slate-800 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">القسم (العربية)</Label>
                  <Input
                    value={formData.department_ar}
                    onChange={(e) => setFormData({...formData, department_ar: e.target.value})}
                    className="bg-slate-950 border-slate-800 mt-1"
                    dir="rtl"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">{t('audits.scope')} *</Label>
                <Textarea
                  value={formData.scope}
                  onChange={(e) => setFormData({...formData, scope: e.target.value})}
                  required
                  rows={3}
                  className="bg-slate-950 border-slate-800 mt-1"
                  data-testid="audit-scope-input"
                />
              </div>

              <div>
                <Label className="text-slate-300">نطاق التدقيق (العربية)</Label>
                <Textarea
                  value={formData.scope_ar}
                  onChange={(e) => setFormData({...formData, scope_ar: e.target.value})}
                  rows={3}
                  className="bg-slate-950 border-slate-800 mt-1"
                  dir="rtl"
                />
              </div>

              {mode === 'edit' && (
                <>
                  <div>
                    <Label className="text-slate-300">Summary</Label>
                    <Textarea
                      value={formData.summary || ''}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      rows={3}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="Executive summary of audit results..."
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Recommendations</Label>
                    <Textarea
                      value={formData.recommendations || ''}
                      onChange={(e) => setFormData({...formData, recommendations: e.target.value})}
                      rows={3}
                      className="bg-slate-950 border-slate-800 mt-1"
                      placeholder="Key recommendations..."
                    />
                  </div>
                </>
              )}

              {/* Asset Selector - Link to Aircraft/Engine/Component */}
              <div className="md:col-span-2">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <AssetSelector
                      value={{
                        aircraft_id: formData.aircraft_id,
                        aircraft_registration: formData.aircraft_registration,
                        engine_id: formData.engine_id,
                        component_id: formData.component_id,
                      }}
                      onChange={(assetData) => {
                        setFormData({
                          ...formData,
                          aircraft_id: assetData.aircraft_id,
                          aircraft_registration: assetData.aircraft_registration,
                          engine_id: assetData.engine_id,
                          component_id: assetData.component_id,
                        });
                      }}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-audit-btn">
                  {mode === 'create' ? t('common.create') : 'Save Changes'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setMode('list'); setSelectedAudit(null); resetForm(); }} className="border-slate-700 text-slate-300">
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // LIST MODE
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="audits-title">
            {t('audits.title')}
          </h1>
          <p className="text-slate-400 mt-1">Manage audit programs, schedules, and findings</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => { setShowIOSADialog(true); fetchIOSATemplates(); }} 
            variant="outline"
            className="border-sky-600 text-sky-400 hover:bg-sky-600/20"
            data-testid="iosa-audit-btn"
          >
            <Plane className="w-4 h-4 mr-2" />IOSA Audit
          </Button>
          <Button onClick={() => setMode('create')} className="bg-sky-600 hover:bg-sky-700" data-testid="new-audit-btn">
            <Plus className="w-4 h-4 mr-2" />{t('audits.newAudit')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search audits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-950 border-slate-800"
                data-testid="audit-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); }}>
              <SelectTrigger className="w-40 bg-slate-950 border-slate-800">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Statuses</SelectItem>
                {AUDIT_STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={(v) => { setFilterType(v); }}>
              <SelectTrigger className="w-40 bg-slate-950 border-slate-800">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Types</SelectItem>
                {AUDIT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAudits} className="border-slate-700 text-slate-300">
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : filteredAudits.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-700" />
            <h3 className="text-xl font-semibold text-white mb-2">No Audits Found</h3>
            <p className="text-slate-400">Schedule a new audit to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAudits.map((audit) => {
            const statusInfo = getStatusInfo(audit.status);
            const typeInfo = getTypeInfo(audit.audit_type);
            const StatusIcon = statusInfo.icon;
            const majorFindings = audit.findings?.filter(f => f.severity === 'major').length || 0;

            return (
              <Card 
                key={audit.id} 
                className={cn(
                  "bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer",
                  majorFindings > 0 && "border-l-4 border-l-red-500"
                )}
                onClick={() => handleViewAudit(audit)}
                data-testid={`audit-card-${audit.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <span className="font-mono text-sm text-sky-400">{audit.audit_number}</span>
                    <Badge className={`${statusInfo.color} border text-xs`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {language === 'ar' ? statusInfo.label_ar : statusInfo.label}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">{audit.title}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between text-slate-400">
                      <span>{language === 'ar' ? typeInfo.label_ar : typeInfo.label}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(audit.planned_date), 'MMM d, yyyy')}
                      </span>
                    </div>
                    {audit.department && (
                      <p className="text-slate-500">{audit.department}</p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                      <span className="text-slate-500">{audit.findings?.length || 0} findings</span>
                      {majorFindings > 0 && (
                        <Badge className="bg-red-600 text-white text-xs">{majorFindings} major</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* IOSA Audit Creation Dialog */}
      <Dialog open={showIOSADialog} onOpenChange={setShowIOSADialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <Plane className="w-5 h-5 text-sky-400" />
              Create IOSA Audit from Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {iosaTemplates && (
              <div className="space-y-2">
                <p className="text-slate-400 text-sm">
                  Select an IOSA section to create an audit with pre-populated checklist items based on ISM Edition 17.
                </p>
                <p className="text-sky-400 text-sm font-medium">
                  Total Standards: {iosaTemplates.total_items} items across {iosaTemplates.total_sections} sections
                </p>
              </div>
            )}

            {/* Section Selection */}
            <div className="space-y-3">
              <Label className="text-white">IOSA Section *</Label>
              <div className="grid grid-cols-2 gap-3">
                {iosaTemplates?.templates?.map((template) => (
                  <button
                    key={template.section}
                    onClick={() => setSelectedIOSASection(template.section)}
                    className={cn(
                      "p-4 rounded-lg border text-left transition-colors",
                      selectedIOSASection === template.section
                        ? "border-sky-500 bg-sky-600/20"
                        : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sky-400 font-bold">{template.section}</span>
                      <Badge className="bg-slate-700 text-slate-300 text-xs">{template.total_items} items</Badge>
                    </div>
                    <p className="text-white font-medium text-sm">{template.section_name}</p>
                    <p className="text-slate-400 text-xs mt-1 line-clamp-2">{template.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Title */}
            <div className="space-y-2">
              <Label className="text-white">Audit Title (Optional)</Label>
              <Input
                value={iosaTitle}
                onChange={(e) => setIOSATitle(e.target.value)}
                placeholder={selectedIOSASection ? `IOSA Audit - ${iosaTemplates?.templates?.find(t => t.section === selectedIOSASection)?.section_name || ''}` : 'Enter custom title...'}
                className="bg-slate-950 border-slate-700"
              />
            </div>

            {/* Scheduled Date */}
            <div className="space-y-2">
              <Label className="text-white">Planned Date</Label>
              <Input
                type="date"
                value={iosaDate}
                onChange={(e) => setIOSADate(e.target.value)}
                className="bg-slate-950 border-slate-700"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIOSADialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateIOSAAudit} 
              className="bg-sky-600 hover:bg-sky-700"
              disabled={!selectedIOSASection || creatingIOSA}
            >
              {creatingIOSA ? 'Creating...' : 'Create IOSA Audit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
